import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'

/**
 * Segmented — a single-select control styled as the "raised thumb" segmented
 * switch (grounded handoff §2.6, option 2c), on the Radix ToggleGroup base
 * (roving-tabindex arrow-key nav for free). It reads as a `role="tablist"` of
 * `role="tab"` segments, each `aria-selected`; the active segment lifts out of
 * the well on a `--card` surface + `--shadow-raised`.
 *
 * The well is `--gray-3`; an inactive segment is muted and, on hover, deepens to
 * `--foreground` over a faint `--gray-4` fill; the active/inactive swap morphs
 * `background / color / box-shadow` on `--duration-base` / `--ease-in-out`.
 * Disabling the whole control (`disabled`) dims the well to `--gray-2`.
 *
 * A segmented control always keeps exactly one segment selected: Radix's
 * single-select would toggle the active segment *off* on re-click (value → `''`),
 * so that empty deselect is ignored. Controlled only — pass `value` +
 * `onValueChange`. `options` are bare strings (value doubles as the label) or
 * objects for a custom label (any node — the theme customizer keeps its icons)
 * or a per-segment `disabled`. Tokens only.
 */

export type SegmentedOption =
	| string
	| { value: string; label?: React.ReactNode; disabled?: boolean }

export type SegmentedProps = {
	/** Controlled selected segment value. */
	value: string
	/** Fires with the newly selected value (never with an empty deselect). */
	onValueChange?: (value: string) => void
	/** The segments: a bare string, or an object for a custom label / per-segment disabled. */
	options: SegmentedOption[]
	/** Disable the whole control — the well dims to `--gray-2`. @default false */
	disabled?: boolean
	className?: string
	/** Accessible name for the tablist. */
	'aria-label'?: string
	'aria-labelledby'?: string
}

/** Widen a bare-string option to the object form (value doubles as the label). */
function normalizeOption(option: SegmentedOption) {
	return typeof option === 'string'
		? { value: option, label: option, disabled: false }
		: {
				value: option.value,
				label: option.label ?? option.value,
				disabled: option.disabled ?? false,
			}
}

function Segmented({
	value,
	onValueChange,
	options,
	disabled = false,
	className,
	...props
}: SegmentedProps) {
	return (
		<ToggleGroupPrimitive.Root
			type="single"
			role="tablist"
			data-slot="segmented"
			value={value}
			// Radix single-select toggles the active item *off* on re-click (value → '');
			// a segmented control always keeps one selected, so ignore the empty deselect.
			onValueChange={(next) => {
				if (next) onValueChange?.(next)
			}}
			disabled={disabled}
			className={cn(
				'inline-flex items-center gap-1 rounded-xl bg-[var(--gray-3)] p-1',
				disabled && 'bg-[var(--gray-2)] opacity-55',
				className,
			)}
			{...props}
		>
			{options.map((option) => {
				const {
					value: optionValue,
					label,
					disabled: optionDisabled,
				} = normalizeOption(option)
				return (
					<ToggleGroupPrimitive.Item
						key={optionValue}
						value={optionValue}
						// Tab semantics (§2.6 a11y) override Radix's single-mode radio role;
						// data-state (from the base) still drives the raised-thumb styling.
						role="tab"
						aria-selected={optionValue === value}
						aria-checked={undefined}
						aria-pressed={undefined}
						disabled={optionDisabled}
						data-slot="segmented-item"
						className={cn(
							'focus-cosy text-body-2xs relative flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-4 py-1.5 font-semibold whitespace-nowrap outline-hidden transition-[background-color,color,box-shadow] duration-[var(--duration-base)] ease-[var(--ease-in-out)] motion-reduce:transition-none',
							// Inactive: muted, with a faint --gray-4 hover fill (enabled only).
							'data-[state=off]:text-muted-foreground data-[state=off]:enabled:hover:text-foreground data-[state=off]:enabled:hover:bg-[oklch(from_var(--gray-4)_l_c_h/0.5)]',
							// Active: the raised thumb lifts out of the well.
							'data-[state=on]:bg-card data-[state=on]:text-foreground data-[state=on]:shadow-raised',
							'disabled:cursor-not-allowed',
						)}
					>
						{label}
					</ToggleGroupPrimitive.Item>
				)
			})}
		</ToggleGroupPrimitive.Root>
	)
}

export { Segmented }
